import {
  AcAutocomplete,
  AcButton,
  AcCheckbox,
  AcContainer,
  AcFormCard,
  AcTable,
  AcTypography,
} from '@components'
import { ICONS, LABELS } from '@constants'
import { useStore } from '@hooks/use-store'
import { ITagRelatedCustomer } from '@typings'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import styles from './tag-related-items.module.scss'

export interface ITagRelatedCustomersTab {
  name?: string
}

export const TagRelatedCustomersTab = observer(
  (_props: ITagRelatedCustomersTab) => {
    const { tags, customer: customersStore } = useStore()
    const [selectedCustomers, setSelectedCustomers] = useState<Set<number>>(
      new Set()
    )
    const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null)
    const [isSearching, setIsSearching] = useState(false)

    const customers = tags.single?.customers || []
    const linkedCustomerIds = new Set(customers.map(c => c.id))

    // Filter search results to exclude already linked customers
    const availableCustomers = customersStore.search_results.filter(c => !linkedCustomerIds.has(c.id))

    const handleSearch = async (query: string) => {
      if (searchDebounce) clearTimeout(searchDebounce)

      if (!query) {
        setIsSearching(false)
        return // Don't query on empty input
      }

      setIsSearching(true)

      const timeout = setTimeout(async () => {
        await customersStore.searchCustomers(query)
        setIsSearching(false)
      }, 300)

      setSearchDebounce(timeout)
    }

    const handleCustomerCheckbox = (customerId: number) => {
      setSelectedCustomers(prev => {
        const newSet = new Set(prev)
        if (newSet.has(customerId)) {
          newSet.delete(customerId)
        } else {
          newSet.add(customerId)
        }
        return newSet
      })
    }

    const handleDetachCustomers = async () => {
      if (selectedCustomers.size === 0) return

      try {
        await tags.detachCustomers(Array.from(selectedCustomers))
        setSelectedCustomers(new Set())
      } catch (e) {
        // Error already handled in store with toast notification
        // Prevent error from bubbling up to React error boundary
      }
    }

    const handleAttachCustomer = async (customer: any) => {
      if (!customer?.id) return
      try {
        await tags.attachCustomers([customer.id])
        // Clear search results after attaching
        customersStore.search_results = []
      } catch (e) {
        // Error already handled in store with toast notification
        // Prevent error from bubbling up to React error boundary
      }
    }

    const handleSelectAllCustomers = () => {
      if (selectedCustomers.size === customers.length) {
        setSelectedCustomers(new Set())
      } else {
        setSelectedCustomers(new Set(customers.map(c => c.id)))
      }
    }

    return (
      <AcContainer>
        <AcFormCard title={LABELS.RELATED_CUSTOMERS}>
          <div className={styles['actions']}>
            <div className={styles['search-container']}>
              <AcAutocomplete
                name="customer_select"
                id="customer_select"
                options={availableCustomers}
                valueKey="id"
                labelKey="name"
                loading={isSearching}
                placeholder={LABELS.SEARCH_CUSTOMERS}
                onSearch={handleSearch}
                onChange={(customer) => {
                  handleAttachCustomer(customer)
                }}
              />
            </div>
            {customers.length > 0 && (
              <div className={styles['button-group']}>
                <AcButton
                  onClick={handleSelectAllCustomers}
                  label={
                    selectedCustomers.size === customers.length
                      ? LABELS.CLEAR
                      : LABELS.SELECT_ALL_ROWS
                  }
                  color="neutral-700"
                />
                <AcButton
                  onClick={handleDetachCustomers}
                  label={LABELS.DETACH_SELECTED}
                  icon={ICONS.DELETE}
                  color="secondary"
                  disabled={selectedCustomers.size === 0}
                />
              </div>
            )}
          </div>

          {customers.length > 0 ? (
            <AcTable>
              <thead>
                <tr>
                  <th></th>
                  <th>{LABELS.NAME}</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer: ITagRelatedCustomer) => (
                  <tr key={customer.id}>
                    <td>
                      <AcCheckbox
                        label={`Select ${customer.name}`}
                        checked={selectedCustomers.has(customer.id)}
                        onClick={() => handleCustomerCheckbox(customer.id)}
                      />
                    </td>
                    <td>{customer.name}</td>
                  </tr>
                ))}
              </tbody>
            </AcTable>
          ) : (
            <AcTypography>{LABELS.NO_CUSTOMERS_LINKED}</AcTypography>
          )}
        </AcFormCard>
      </AcContainer>
    )
  }
)
